import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { MapPin, Plus, Edit2, Trash2, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = [
  { code: "LU", label: "Luxembourg", placeholder: "ex. 1234 (4 chiffres)" },
  { code: "FR", label: "France", placeholder: "ex. 75001 (5 chiffres)" },
  { code: "BE", label: "Belgique", placeholder: "ex. 1000 (4 chiffres)" },
];
const COUNTRY_LABELS = { LU: "Luxembourg", FR: "France", BE: "Belgique" };

const empty = { full_name: "", line1: "", line2: "", postal_code: "", city: "", country: "LU", phone: "", is_default: false };

function AddressForm({ initial, onSubmit, onCancel, hideDefaultToggle }) {
  const [f, setF] = useState(initial || empty);
  const set = (k, v) => setF({ ...f, [k]: v });
  const country = COUNTRIES.find((c) => c.code === f.country);

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-4">
      <div><Label>Nom complet</Label><Input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} required data-testid="address-form-fullname" /></div>
      <div><Label>Adresse (ligne 1)</Label><Input value={f.line1} onChange={(e) => set("line1", e.target.value)} required data-testid="address-form-line1" /></div>
      <div><Label>Adresse (ligne 2, optionnel)</Label><Input value={f.line2 || ""} onChange={(e) => set("line2", e.target.value)} data-testid="address-form-line2" /></div>
      <div>
        <Label>Pays</Label>
        <Select value={f.country} onValueChange={(v) => set("country", v)}>
          <SelectTrigger data-testid="address-form-country"><SelectValue /></SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Code postal</Label><Input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} required placeholder={country?.placeholder} data-testid="address-form-postal" /></div>
        <div><Label>Ville</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} required data-testid="address-form-city" /></div>
      </div>
      <div><Label>Téléphone (optionnel)</Label><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="address-form-phone" /></div>
      {!hideDefaultToggle && (
        <label className="flex items-center justify-between border border-[#E5EAE5] rounded-xl px-4 py-3 cursor-pointer">
          <span className="text-sm font-medium">Définir comme adresse par défaut</span>
          <Switch checked={f.is_default} onCheckedChange={(v) => set("is_default", v)} data-testid="address-form-default" />
        </label>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
        <Button type="submit" className="bg-[#7AC143] hover:bg-[#67BC2A]" data-testid="address-form-submit">Enregistrer</Button>
      </div>
    </form>
  );
}

export function ShippingAddresses({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/addresses");
      setItems(r.data);
      if (onChange) onChange(r.data);
    } catch (err) {
      // anonymous user (401) is expected — don't surface to UI
      if (err?.response?.status !== 401) console.debug("[ShippingAddresses] load failed:", err);
    }
  }, [onChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async (f) => {
    try {
      if (editing) await api.put(`/addresses/${editing.id}`, f);
      else await api.post("/addresses", f);
      toast.success("Adresse enregistrée");
      setOpen(false); setEditing(null);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Erreur");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette adresse ?")) return;
    await api.delete(`/addresses/${id}`);
    toast.success("Adresse supprimée");
    refresh();
  };

  const setDefault = async (id) => {
    await api.post(`/addresses/${id}/default`);
    toast.success("Adresse par défaut mise à jour");
    refresh();
  };

  return (
    <div data-testid="shipping-addresses">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-black tracking-tight">Adresses de livraison</h2>
          <p className="text-[#4B634B] text-sm mt-1">Plusieurs adresses possibles · 🇱🇺 🇫🇷 🇧🇪</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#7AC143] hover:bg-[#67BC2A]" data-testid="address-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouvelle"} adresse de livraison</DialogTitle></DialogHeader>
            <AddressForm initial={editing} onSubmit={save} onCancel={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5EAE5] rounded-2xl p-12 text-center">
          <MapPin className="w-12 h-12 text-[#7AC143] mx-auto mb-3" />
          <p className="text-[#4B634B]">Aucune adresse enregistrée. Ajoutez votre première adresse pour commander.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`address-card-${a.id}`} className={`bg-white border rounded-2xl p-5 ${a.is_default ? "border-[#7AC143]" : "border-[#E5EAE5]"}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="font-bold">{a.full_name}</div>
                {a.is_default && <Badge className="bg-[#7AC143]"><CheckCircle2 className="w-3 h-3 mr-1" /> Par défaut</Badge>}
              </div>
              <p className="text-sm text-[#4B634B] leading-relaxed">
                {a.line1}{a.line2 ? <><br/>{a.line2}</> : null}<br/>
                {a.postal_code} {a.city}<br/>
                {COUNTRY_LABELS[a.country]}
                {a.phone ? <><br/>📞 {a.phone}</> : null}
              </p>
              <div className="flex gap-2 mt-4">
                {!a.is_default && (
                  <Button size="sm" variant="outline" onClick={() => setDefault(a.id)} data-testid={`address-default-${a.id}`}>
                    Définir par défaut
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setOpen(true); }} data-testid={`address-edit-${a.id}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del(a.id)} data-testid={`address-delete-${a.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BillingAddress() {
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/billing-address");
      setItem(r.data);
    } catch (err) {
      // anonymous user (401) is expected — don't surface to UI
      if (err?.response?.status !== 401) console.debug("[BillingAddress] load failed:", err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async (f) => {
    try {
      await api.put("/billing-address", f);
      toast.success("Adresse de facturation enregistrée");
      setOpen(false);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Erreur");
    }
  };

  const del = async () => {
    if (!window.confirm("Supprimer l'adresse de facturation ?")) return;
    await api.delete("/billing-address");
    toast.success("Supprimée");
    refresh();
  };

  return (
    <div data-testid="billing-address">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-black tracking-tight">Adresse de facturation</h2>
          <p className="text-[#4B634B] text-sm mt-1">Une seule adresse · obligatoire pour valider une commande</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#7AC143] hover:bg-[#67BC2A]" data-testid="billing-edit-btn">
              {item ? <><Edit2 className="w-4 h-4 mr-2" /> Modifier</> : <><Plus className="w-4 h-4 mr-2" /> Ajouter</>}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Adresse de facturation</DialogTitle></DialogHeader>
            <AddressForm initial={item} onSubmit={save} onCancel={() => setOpen(false)} hideDefaultToggle />
          </DialogContent>
        </Dialog>
      </div>

      {!item ? (
        <div className="bg-white border border-dashed border-[#FACC15] bg-[#FACC15]/10 rounded-2xl p-12 text-center" data-testid="billing-empty">
          <FileText className="w-12 h-12 text-[#FACC15] mx-auto mb-3" />
          <p className="text-[#1A2E1A] font-semibold">Adresse de facturation manquante</p>
          <p className="text-[#4B634B] text-sm mt-1">Vous ne pourrez pas finaliser de commande sans adresse de facturation.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5EAE5] rounded-2xl p-5 max-w-md" data-testid="billing-card">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#7AC143] mb-2">
            <FileText className="w-3.5 h-3.5" /> Facturation
          </div>
          <div className="font-bold">{item.full_name}</div>
          <p className="text-sm text-[#4B634B] leading-relaxed mt-1">
            {item.line1}{item.line2 ? <><br/>{item.line2}</> : null}<br/>
            {item.postal_code} {item.city}<br/>
            {COUNTRY_LABELS[item.country]}
            {item.phone ? <><br/>📞 {item.phone}</> : null}
          </p>
          <Button size="sm" variant="ghost" onClick={del} className="mt-3 text-red-500 hover:text-red-700" data-testid="billing-delete">
            <Trash2 className="w-4 h-4 mr-1" /> Supprimer
          </Button>
        </div>
      )}
    </div>
  );
}

// Default export kept for backward compatibility
export default ShippingAddresses;
